/**
 * Elevación clínica por ticket en esta pestaña (post-canje elev).
 * Memoria + sessionStorage: sobrevive strip de ?elev= y remounts;
 * no filtra a share-safe en otra pestaña (sessionStorage vacío allí).
 */
import { ENTITLEMENT_SOURCES, UDV_PERSONAS, UDV_PLANS } from './constants'
import { parseThemeQueryParam } from './chromeTheme'
import { isClinicalPlan, pickProfileForTicketLoad } from './clinicalElevation'

const STORAGE_KEY = 'udv.platformElevation.v1'
const DEFAULT_TTL_MS = 8 * 60 * 60 * 1000

/** @type {PlatformElevationSession|null} */
let memorySession = null

/**
 * @typedef {{
 *   ticketId: string,
 *   plan: string,
 *   shell: string|null,
 *   theme: string|null,
 *   edgeId: string|null,
 *   exp: number,
 *   leaseToken?: string|null,
 *   leaseExpiresAt?: string|null,
 *   extendIntervalSeconds?: number|null,
 *   ticketApiBase?: string|null,
 * }} PlatformElevationSession
 */

function canUseSessionStorage() {
  try {
    return typeof window !== 'undefined' && !!window.sessionStorage
  } catch (e) {
    return false
  }
}

/**
 * @param {PlatformElevationSession|null|undefined} v
 * @returns {PlatformElevationSession|null}
 */
function normalizeSession(v) {
  if (!v || typeof v !== 'object') return null
  const ticketId = v.ticketId != null ? String(v.ticketId).trim() : ''
  const plan = v.plan != null ? String(v.plan).trim() : ''
  const exp = Number(v.exp) || 0
  if (!ticketId || !isClinicalPlan(plan) || exp <= Date.now()) {
    return null
  }
  const leaseToken =
    v.leaseToken != null && String(v.leaseToken).trim() !== ''
      ? String(v.leaseToken).trim()
      : null
  const intervalN = Number(v.extendIntervalSeconds)
  return {
    ticketId,
    plan,
    shell: v.shell != null ? String(v.shell) : null,
    theme: parseThemeQueryParam(v.theme),
    edgeId: v.edgeId != null ? String(v.edgeId) : null,
    exp,
    leaseToken,
    leaseExpiresAt:
      v.leaseExpiresAt != null && String(v.leaseExpiresAt).trim() !== ''
        ? String(v.leaseExpiresAt).trim()
        : null,
    extendIntervalSeconds:
      Number.isFinite(intervalN) && intervalN >= 30
        ? Math.floor(intervalN)
        : null,
    ticketApiBase:
      v.ticketApiBase != null && String(v.ticketApiBase).trim() !== ''
        ? String(v.ticketApiBase).replace(/\/+$/, '')
        : null,
  }
}

/**
 * @returns {PlatformElevationSession|null}
 */
export function loadPlatformElevationSession() {
  const fromMem = normalizeSession(memorySession)
  if (fromMem) return fromMem
  if (fromMem === null && memorySession) {
    memorySession = null
  }

  if (!canUseSessionStorage()) return null
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = normalizeSession(JSON.parse(raw))
    if (!parsed) {
      clearPlatformElevationSession()
      return null
    }
    memorySession = parsed
    return parsed
  } catch (e) {
    return null
  }
}

/**
 * @param {{
 *   ticketId: string,
 *   plan: string,
 *   shell?: string|null,
 *   theme?: string|null,
 *   edgeId?: string|null,
 *   ttlMs?: number,
 *   leaseToken?: string|null,
 *   leaseExpiresAt?: string|null,
 *   extendIntervalSeconds?: number|null,
 *   ticketApiBase?: string|null,
 * }} opts
 */
export function savePlatformElevationSession(opts) {
  const ticketId = opts && opts.ticketId != null ? String(opts.ticketId).trim() : ''
  const plan = opts && opts.plan != null ? String(opts.plan).trim() : ''
  if (!ticketId || !isClinicalPlan(plan)) return
  const ttl = Number(opts.ttlMs) > 0 ? Number(opts.ttlMs) : DEFAULT_TTL_MS
  const leaseToken =
    opts.leaseToken != null && String(opts.leaseToken).trim() !== ''
      ? String(opts.leaseToken).trim()
      : null
  const intervalN = Number(opts.extendIntervalSeconds)
  const payload = {
    ticketId,
    plan,
    shell: opts.shell != null ? String(opts.shell) : null,
    theme: parseThemeQueryParam(opts.theme),
    edgeId: opts.edgeId != null ? String(opts.edgeId) : null,
    exp: Date.now() + ttl,
    leaseToken,
    leaseExpiresAt:
      opts.leaseExpiresAt != null && String(opts.leaseExpiresAt).trim() !== ''
        ? String(opts.leaseExpiresAt).trim()
        : null,
    extendIntervalSeconds:
      Number.isFinite(intervalN) && intervalN >= 30
        ? Math.floor(intervalN)
        : null,
    ticketApiBase:
      opts.ticketApiBase != null && String(opts.ticketApiBase).trim() !== ''
        ? String(opts.ticketApiBase).replace(/\/+$/, '')
        : null,
    source: ENTITLEMENT_SOURCES.PLATFORM_ELEVATION,
  }
  memorySession = payload
  if (!canUseSessionStorage()) return
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload))
  } catch (e) {
    /* memoria alcanza en esta pestaña */
  }
}

export function clearPlatformElevationSession() {
  memorySession = null
  if (!canUseSessionStorage()) return
  try {
    window.sessionStorage.removeItem(STORAGE_KEY)
  } catch (e) {
    /* ignore */
  }
}

/**
 * @param {string|null|undefined} ticketId
 * @returns {PlatformElevationSession|null}
 */
export function matchPlatformElevationSession(ticketId) {
  const want = ticketId != null ? String(ticketId).trim() : ''
  if (!want) return null
  const s = loadPlatformElevationSession()
  if (!s || s.ticketId !== want) return null
  return s
}

/**
 * @param {PlatformElevationSession} session
 * @param {string} ticketProfile
 */
export function platformElevationSessionToAccess(session, ticketProfile) {
  const plan = session.plan || UDV_PLANS.LITE
  return {
    elevated: true,
    plan,
    persona: UDV_PERSONAS.CLINICAL,
    loadProfile: pickProfileForTicketLoad(ticketProfile, true, plan),
    shell: session.shell,
    theme: session.theme || null,
    entitlements: {
      plan,
      shell: session.shell,
      theme: session.theme || null,
      pacsId: null,
      udvToken: null,
      edgeId: session.edgeId,
      clinicName: null,
      source: ENTITLEMENT_SOURCES.PLATFORM_ELEVATION,
    },
    source: ENTITLEMENT_SOURCES.PLATFORM_ELEVATION,
    mode: 'clinical',
  }
}
